import { useState } from "react";
import {
    Button,
    Dialog,
    DialogHeader,
    DialogBody,
    DialogFooter,
    IconButton,
} from "@material-tailwind/react";
import { FormProvider, get, useForm, useFormContext } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import RHFPasswordInput from "components/password-input/rhf-password-input";
import RHFInput from "components/form/rhf-input";

// Confirm password field with show/hide toggle. Hoisted to module scope:
// defined inside the form component it would get a new component type on
// every parent render, and the form child re-renders on formState changes,
// so the field would unmount/remount mid-typing, dropping focus and the
// show/hide state. It needs nothing from the parent closure; its one call
// site always passed the same constants, so they are hardcoded here.
const ConfirmPasswordField = () => {
    const { formState: { errors } } = useFormContext();
    const [showPassword, setShowPassword] = useState(false);
    const error = get(errors, "confirm_password");

    return (
        <div className="mb-4 font-body rounded-xl relative space-y-2">
            <div className="mb-4 font-body rounded-xl relative">
                <RHFInput
                    name="confirm_password"
                    type={showPassword ? "text" : "password"}
                    label="Confirm New Password"
                    color="gray"
                    autoComplete="new-password"
                    className="pr-10"
                />
                <IconButton
                    variant="text"
                    size="sm"
                    ripple={false}
                    onClick={() => setShowPassword((prev) => !prev)}
                    className="!absolute right-2 top-2 my-auto rounded-full"
                >
                    <span className="material-icons text-gray-600">
                        {showPassword ? "visibility_off" : "visibility"}
                    </span>
                </IconButton>
            </div>
            {error && (
                <div className="text-xs text-red-600 mt-1">
                    {error.message}
                </div>
            )}
        </div>
    );
};

// Lives inside the <Dialog> so every open remounts the form with fresh
// empty passwords and the current email prop (recipe P5). No reset
// effect: the email never changes while the dialog is open.
function UpdatePasswordForm({ email, handlePasswordSubmit, handleClose }) {
    // Validation schema
    const validationSchemaPassword = Yup.object().shape({
        password: Yup.string()
            .min(8, "Password must be at least 8 characters")
            // See resetconfirm.js: verbatim passwords (#1097) mean this is the
            // only thing rejecting an all-whitespace credential. This schema is
            // the one that runs; account.js passes a validationSchemaPassword
            // prop this component does not accept.
            .matches(/\S/, "Password cannot be only spaces")
            .required("Required"),
        confirm_password: Yup.string()
            .required("Required")
            .oneOf([Yup.ref("password"), null], "Passwords must match")
            .min(8, "Password must be at least 8 characters"),
    });

    const methods = useForm({
        mode: "onChange",
        resolver: yupResolver(validationSchemaPassword),
        defaultValues: {
            email: email || "",
            password: "",
            confirm_password: "",
        },
    });
    // Read both flags up front: formState is a subscription proxy, so an
    // inline `!isDirty || !isValid` short-circuits before isValid while
    // isDirty is false, and the validity updates never arrive.
    const { isValid, isDirty } = methods.formState;

    return (
        <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(handlePasswordSubmit)}>
                <DialogBody className="dialog__body">
                    <div className="mb-4 font-body p-4 card-inner-body rounded-xl">
                        {email && (
                            <div className="mb-4">
                                {/* Read-only username binding for
                                    password managers (#1078/#1112);
                                    account.js strips it from the
                                    submit payload. */}
                                <RHFInput
                                    name="email"
                                    label="Email"
                                    autoComplete="username"
                                    readOnly
                                />
                            </div>
                        )}
                        <div className="mb-4">
                            <RHFPasswordInput
                                name="password"
                                label="New Password"
                                color="gray"
                                autoComplete="new-password"
                                showStrength={true}
                            />
                        </div>

                        <ConfirmPasswordField />
                    </div>
                </DialogBody>
                <DialogFooter className="dialog__footer">
                    <div className="bottom-4">
                        <Button
                            type="button"
                            className="btn btn--secondary mr-2"
                            variant="filled"
                            onClick={handleClose}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            disabled={!isDirty || !isValid}
                            onClick={handleClose}
                            className="btn btn--primary"
                            variant="filled"
                        >
                            Save
                        </Button>
                    </div>
                </DialogFooter>
            </form>
        </FormProvider>
    );
}

export default function UpdatePasswordDialog({
    email,
    handlePasswordSubmit,
    showUpdatePasswordDialog,
    setShowUpdatePasswordDialog,
}) {
    const handleClose = () => {
        setShowUpdatePasswordDialog((show) => !show);
    };

    return (
        <Dialog
            open={showUpdatePasswordDialog}
            handler={handleClose}
            className="dialog"
        >
            <DialogHeader className="dialog__header">
                Update Password
            </DialogHeader>
            <UpdatePasswordForm
                email={email}
                handlePasswordSubmit={handlePasswordSubmit}
                handleClose={handleClose}
            />
        </Dialog>
    );
}
